import * as config from '../constants.js';
import { calculateXPToNextLevel, isSynergyRevealed, isSynergyUnlocked, maxHpForLevel } from './character-core.js'
import { getRandomAvatar, findItem, getTechniqueSkill } from "./util.js";
import { gameState } from './gameState-core.js';
import { getSkillSynergyPartners, getSynergySkillsForTechnique } from './eventSystem-core.js';
import { debug, isDebug, CH } from './debugLog.js';

/**
 * Contractor Core Logic - Pure Functions
 *
 * Pure game logic for contractor generation and selection that has no browser dependencies.
 * Can be used in both browser (contractor.js) and Node.js (simulator) contexts.
 *
 * Extracted from contractor.js to separate pure logic from UI updates and Alpine.js dependencies.
 */

// Contractor-list polling cadence. Listings change rarely, so a contractors page
// left open shouldn't keep polling every 10s forever. Each successive poll
// doubles the delay until it passes CONTRACTOR_POLL_MAX_MS, settling at 320s
// (~5m20s — the first step beyond 5 minutes). Whenever the section isn't on
// screen the caller resets to the minimum, so reopening it polls promptly again.
export const CONTRACTOR_POLL_MIN_MS = 10 * 1000;
export const CONTRACTOR_POLL_MAX_MS = 5 * 60 * 1000;

/**
 * Next delay in the contractor poll backoff. Pure so the cadence is testable.
 * Sequence: 10s -> 20s -> 40s -> 80s -> 160s -> 320s, then holds at 320s.
 * @param {number} currentDelay current delay in ms
 * @returns {number} the delay to use for the next poll
 */
export function nextContractorPollDelay(currentDelay) {
  // Anything missing or below the floor restarts the sequence.
  if (!Number.isFinite(currentDelay) || currentDelay < CONTRACTOR_POLL_MIN_MS) {
    return CONTRACTOR_POLL_MIN_MS;
  }
  // Once past the cap the cadence has settled — stop doubling.
  if (currentDelay > CONTRACTOR_POLL_MAX_MS) return currentDelay;
  return currentDelay * 2;
}

/**
 * Generate a pool of contractors with faction-specific skills and levels
 * Pure function - no gameState or Alpine dependencies
 * @param {number} playerLevel - Current character level
 * @returns {Array} Array of generated contractor objects
 */
/**
 * Per-skill synergy status for a contractor card, judged against the
 * prospective team (player + reserved/hired contractors + this contractor):
 * - ready: a partner technique for the skill's events is already equipped
 *   somewhere on that team - synergy variants can fire.
 * - swap: no partner equipped, but the player OWNS a partner device
 *   (equipping is non-destructive, so owned = in inventory) - one gear swap
 *   away from enabling the synergy.
 * Also reports which of the contractor's own gear pieces partner skills the
 * player has (the reverse direction: why hiring THEM upgrades YOUR events).
 *
 * Returns { skills: {skillId: {state, techniqueId, gearName?, gearIcon?}},
 *           gearPartners: {techniqueId: [skillIds the player has]},
 *           hasReady, hasSwap }
 */
export function getContractorSynergyStatus(contractor) {
  // Progressive disclosure: no synergy indicators before the layer is
  // revealed (character below synergyUnlocks.minLevel)
  if (!isSynergyRevealed()) {
    return { skills: {}, gearPartners: {}, hasReady: false, hasSwap: false };
  }

  const teamTechniques = new Set();
  const addGear = c => Object.keys(c?.equippedTechniqueGear || {}).forEach(t => teamTechniques.add(t));
  addGear(gameState.character);
  (gameState.currentRaid?.reservedContractors || []).forEach(addGear);
  (gameState.currentRaid?.hiredContractors || []).forEach(addGear);
  addGear(contractor);

  const playerTechniques = new Set(Object.keys(gameState.character?.equippedTechniqueGear || {}));

  const ownedDevices = Object.entries(gameState.character?.inventory || {})
    .filter(([, qty]) => qty > 0)
    .map(([id]) => findItem('id', id))
    .filter(item => item?.type === 'techGear');

  // Either-side unlock rule (mirrors variant selection): a partner technique
  // counts only if the event skill is unlocked, or it is the player's own
  // gear from an unlocked skill
  const partnerAllowed = (eventSkillId, techniqueId, isPlayerDevice) =>
    isSynergyUnlocked(eventSkillId) ||
    (isPlayerDevice && isSynergyUnlocked(getTechniqueSkill(techniqueId)));

  const skills = {};
  Object.keys(contractor?.skills || {}).forEach(skillId => {
    const partners = getSkillSynergyPartners(skillId);
    if (partners.size === 0) return;
    const ready = [...partners].find(t =>
      teamTechniques.has(t) && partnerAllowed(skillId, t, playerTechniques.has(t)));
    if (ready) {
      skills[skillId] = { state: 'ready', techniqueId: ready };
      return;
    }
    const swap = ownedDevices.find(d =>
      partners.has(d.techniqueId) && partnerAllowed(skillId, d.techniqueId, true));
    if (swap) {
      skills[skillId] = { state: 'swap', techniqueId: swap.techniqueId, gearName: swap.name, gearIcon: swap.icon };
    }
  });

  // Reverse direction: contractor gear that upgrades skills the player has
  // (event-side unlock applies - the event is the player's skill)
  const playerSkills = new Set(Object.keys(gameState.character?.skills || {}));
  const gearPartners = {};
  Object.keys(contractor?.equippedTechniqueGear || {}).forEach(techniqueId => {
    const upgradable = [...getSynergySkillsForTechnique(techniqueId)]
      .filter(s => playerSkills.has(s) && isSynergyUnlocked(s));
    if (upgradable.length > 0) gearPartners[techniqueId] = upgradable;
  });

  const states = Object.values(skills).map(s => s.state);
  return {
    skills,
    gearPartners,
    hasReady: states.includes('ready') || Object.keys(gearPartners).length > 0,
    hasSwap: states.includes('swap')
  };
}

/**
 * Generate an AI contractor's equipped skill gear: up to min(2, level/4)
 * pieces, each the epic device for a random technique of a known skill
 * (one per skill). Returns the same {techniqueId: gearItem} shape the
 * player's equippedTechniqueGear uses.
 */
export function generateContractorGear(factionKey, skills, level) {
  const equipped = {};
  const maxPieces = Math.min(2, Math.floor(level / 4));
  if (maxPieces <= 0) return equipped;

  const capitalized = factionKey.charAt(0).toUpperCase() + factionKey.slice(1);
  const devices = config[`itemsEpicDevice${capitalized}`] || [];
  const techniques = config.skillTechniques[factionKey] || [];

  const knownSkills = Object.keys(skills).sort(() => Math.random() - 0.5).slice(0, maxPieces);
  for (const skill of knownSkills) {
    const skillTechniques = techniques.filter(t => t.skill === skill);
    if (skillTechniques.length === 0) continue;
    const technique = skillTechniques[Math.floor(Math.random() * skillTechniques.length)];
    const device = devices.find(d => d.techniqueId === technique.id);
    if (device) equipped[technique.id] = device;
  }
  return equipped;
}

export function generateContractorsCore(playerLevel) {
  let newContractors = [];
  let contractorId = -1; // Negative IDs for AI contractors

  const baseSkillMin = Math.max(1, Math.floor(playerLevel / 3));
  const baseSkillMax = Math.max(3, playerLevel + Math.floor(playerLevel / 2));
  const skillRange = baseSkillMax - baseSkillMin;

  Object.keys(config.factionSkills).forEach((factionKey) => {
    const faction = config.factionSkills[factionKey];

    for (let i = 0; i < 5; i++) {
      // Generate faction-specific name
      const factionNameData = config.factionNames[factionKey];
      const firstName = factionNameData.names[Math.floor(Math.random() * factionNameData.names.length)];
      const lastName = factionNameData.surnames[Math.floor(Math.random() * factionNameData.surnames.length)];
      const name = firstName + " " + lastName;
      const skills = {};
      const factionSkills = Object.keys(faction.skills);

      let rewardPercentage = 10;
      // Give each contractor 2-3 random skills from their faction
      const numSkills = 2 + Math.floor(Math.random() * 2);
      for (let j = 0; j < numSkills; j++) {
        const skill = factionSkills[Math.floor(Math.random() * factionSkills.length)];
        const addlPoints = Math.round(Math.random() * skillRange);
        skills[skill] = baseSkillMin + addlPoints;
        rewardPercentage += Math.ceil(5 * (addlPoints/skillRange) * (addlPoints/skillRange));
      }

      // Calculate contractor level based on skill levels
      const skillLevels = Object.values(skills);
      const bestSkill = skillLevels.length > 0 ? Object.keys(skills).reduce((a, b) => (skills[a] > skills[b] ? a : b)) : null;
      const highestSkillLevel = skillLevels.length > 0 ? Math.max(...skillLevels) : 0;
      const avgSkillLevel = skillLevels.reduce((sum, level) => sum + level, 0) / skillLevels.length;
      const contractorLevel = Math.max(1, Math.floor(avgSkillLevel / 2) + Math.floor(Math.random() * 3));
      const flavorText = generateContractorFlavorBySkill(bestSkill);
      debug(CH.events, "Generated contractor flavor:", flavorText);

      const contractor = {
        id: -(newContractors.length + 1),
        name: name,
        faction: factionKey,
        skills: skills,
        rewardPercentage: rewardPercentage,
        hired: false,
        avatar: getRandomAvatar(factionKey), // legacy sprite class, kept as fallback
        // Procedural portrait seed. A fresh persisted token, NOT the `id` above —
        // that id is a reused negative slot index, so seeding from it would make
        // every slot-N contractor share a face. This token is stable once saved.
        avatarSeed: `ctr_${factionKey}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        level: contractorLevel,
        xp: Math.floor(Math.random() * calculateXPToNextLevel(contractorLevel)), // Random XP progress
        // Was `100 + (level-1) * gainAt(level)`, which multiplies the CURRENT
        // level's gain across every level instead of summing each level's own
        // gain - it over-paid increasingly with level (1300 HP at level 25 vs
        // the curve's 840). maxHpForLevel is the one definition of the curve.
        maxHp: maxHpForLevel(contractorLevel),
        currentHp: null, // Will be set to maxHp
        isAlive: true,
        injuries: [],
        title: flavorText ? flavorText.role : "Contractor",
        quote: flavorText ? flavorText.quote : '"Ready for work."',
      };

      // Equipped skill gear: seasoned contractors come tooled up (0-2 pieces
      // by level, one per known skill - same one-per-skill rule as the
      // player). Their gear feeds team synergy variants and success bonuses,
      // making WHO you hire matter beyond raw skill numbers.
      contractor.equippedTechniqueGear = generateContractorGear(factionKey, skills, contractorLevel);

      // Set current HP to max HP
      contractor.currentHp = contractor.maxHp;
      newContractors.push(contractor);
    }
  });

  // Preserve hired and reserved contractors by adding them to the new pool
  // Only preserve NPC contractors (negative IDs) - player contractors don't belong in the pool
  const preservedContractors = [
    ...(gameState.currentRaid.hiredContractors || []).filter((c) => c.id < 0),
    ...(gameState.currentRaid.reservedContractors || []).filter((c) => c.id < 0),
  ];

  if (preservedContractors.length > 0) {
    const preservedIds = new Set(preservedContractors.map((c) => c.id));
    // Remove any generated contractors that have the same ID as preserved ones
    const dedupedNew = newContractors.filter((c) => !preservedIds.has(c.id));
    newContractors = [...dedupedNew, ...preservedContractors];
  }

  return newContractors;
}

/**
 * Select best contractors for a raid based on path skills
 * Prioritizes contractors that have skills needed for the raid path
 * Max 2 contractors can be selected
 * @param {Array} contractors - Available contractor pool
 * @param {Array} path - Raid path (array of step objects with room data)
 * @param {Object} characterSkills - Character's current skill levels
 * @returns {Array} Selected contractors (max 2)
 */
export function selectBestContractorsForPathCore(contractors, path, characterSkills) {
  if (!contractors || contractors.length === 0 || !path) return [];

  // Extract skills needed from the path
  const pathSkills = new Set();
  for (const step of path) {
    if (step.room && step.room.primary) {
      pathSkills.add(step.room.primary);
      if (step.room.support && Array.isArray(step.room.support)) {
        step.room.support.forEach(skill => pathSkills.add(skill));
      }
    }
  }

  // Helper function to calculate individual contractor score
  function scoreContractor(contractor) {
    let score = 0;
    const contractorSkillIds = Object.keys(contractor.skills || {});

    // Count how many path-required skills this contractor has
    const matchedSkills = contractorSkillIds.filter(skill => pathSkills.has(skill));
    const unmatchedPathSkills = Array.from(pathSkills).filter(skill =>
      !contractorSkillIds.includes(skill) && (characterSkills[skill] || 0) === 0
    );

    // Score: contractor skills that cover missing path skills are valuable
    for (const skill of matchedSkills) {
      const skillLevel = contractor.skills[skill];
      if ((characterSkills[skill] || 0) === 0) {
        // Character doesn't have this skill, contractor fill-in is valuable
        score += 200 + (skillLevel * 20);
      } else {
        // Character has skill but contractor's level helps
        score += 50 + (skillLevel * 10);
      }
    }

    // Penalize contractors with many uncovered path skills
    score -= unmatchedPathSkills.length * 30;

    // Small bonus for higher level contractors
    score += contractor.level * 5;

    return score;
  }

  // Helper function to evaluate pair synergy
  function scorePair(contractor1, contractor2) {
    const skills1 = new Set(Object.keys(contractor1.skills || {}));
    const skills2 = new Set(Object.keys(contractor2.skills || {}));

    let pairScore = scoreContractor(contractor1) + scoreContractor(contractor2);

    // Bonus for complementary skills (skills that only one has)
    const uniqueSkills = new Set([...skills1, ...skills2]);
    const sharedSkills = new Set([...skills1].filter(s => skills2.has(s)));
    const complementarySkills = uniqueSkills.size - sharedSkills.size;

    // Reward diversity: contractors with different skills complement each other
    pairScore += complementarySkills * 15;

    // Slight penalty for overlapping skills (unless covering critical gaps)
    pairScore -= sharedSkills.size * 3;

    return pairScore;
  }

  // Find the best pair of contractors
  let bestPair = [];
  let bestScore = -Infinity;

  // Try all possible pairs
  for (let i = 0; i < contractors.length; i++) {
    for (let j = i + 1; j < contractors.length; j++) {
      const pairScore = scorePair(contractors[i], contractors[j]);
      if (pairScore > bestScore) {
        bestScore = pairScore;
        bestPair = [contractors[i], contractors[j]];
      }
    }
  }

  // If no good pair found, also consider single contractors
  if (bestScore <= 0 && contractors.length > 0) {
    let bestSingle = contractors[0];
    let bestSingleScore = scoreContractor(contractors[0]);

    for (let i = 1; i < contractors.length; i++) {
      const singleScore = scoreContractor(contractors[i]);
      if (singleScore > bestSingleScore) {
        bestSingleScore = singleScore;
        bestSingle = contractors[i];
      }
    }

    if (bestSingleScore > 0) {
      return [bestSingle];
    }
  }

  return bestScore > 0 ? bestPair : [];
}

// Generate random contractors
export function generateContractorFlavorBySkill(bestSkill) {
  const data = config.contractorSkillFlavors[bestSkill];
  if (!data) return null;
  const role = data.roles[Math.floor(Math.random() * data.roles.length)];
  const quote = data.quotes[Math.floor(Math.random() * data.quotes.length)];
  return { role, quote };
}

/**
 * Swap equipped technique gear so it synergises with the contractors actually
 * hired for this raid.
 *
 * Gear coverage is NOT simply "one device per skill" - that maxes the flat +10%
 * device bonus, but synergy variants are the bigger prize, and whether one can
 * fire depends on the pairing between the player's tooled technique and what the
 * hired team brings. A kit that synergised with last raid's contractors may
 * synergise with none of this raid's. The game states the intent directly
 * (character.js equipTechniqueGear): "The full kit is a toolbox - swap freely
 * between raids to match the route." It also already computes the advice for the
 * player - contractor-core's getContractorSynergyStatus returns a 'swap' state
 * naming the device to equip - so this is the player acting on a prompt the game
 * gives them, not new strategy.
 *
 * Greedy single pass over the skills the route tests. Where a swap is needed it
 * prefers giving up a device whose own skill the route does NOT test, since the
 * one-per-skill rule means equipping a partner evicts same-skill gear.
 *
 * Assumption worth stating: a synergy variant is treated as worth more than the
 * flat bonus given up (10% x 1/3 = 3.3pp). True for loot-bearing variants; not
 * separately verified per variant.
 */
export function swapGearForSynergy(char, path, contractors) {
  if (!char.equippedTechniqueGear) char.equippedTechniqueGear = {};

  const teamTechniques = new Set(Object.keys(char.equippedTechniqueGear));
  (contractors || []).forEach(c =>
    Object.keys(c?.equippedTechniqueGear || {}).forEach(t => teamTechniques.add(t)));

  const ownedDevices = Object.entries(char.inventory || {})
    .filter(([, qty]) => qty > 0)
    .map(([id]) => findItem('id', id))
    .filter(item => item?.type === 'techGear' && item.techniqueId);
  if (ownedDevices.length === 0) return 0;

  const routeSkills = new Set();
  (path || []).forEach(step => { if (step.room?.primary) routeSkills.add(step.room.primary); });

  // Either-side unlock rule, mirroring eventSystem-core's variant selection and
  // contractor-core's partnerAllowed: a partner counts when the EVENT's skill is
  // unlocked, OR when it is the player's own device from an unlocked skill.
  // Only checking the event side made this a no-op - a region's rooms span
  // factions, and a player only ever unlocks synergies on their own faction's
  // skills, so the event-side test almost never passed.
  const partnerAllowed = (eventSkillId, techniqueId) =>
    isSynergyUnlocked(eventSkillId, char) ||
    isSynergyUnlocked(getTechniqueSkill(techniqueId), char);

  let swaps = 0;
  for (const skillId of routeSkills) {
    const partners = getSkillSynergyPartners(skillId);
    if (partners.size === 0) continue;
    // Already covered by the player's or a hired contractor's kit?
    if ([...partners].some(t => teamTechniques.has(t) && partnerAllowed(skillId, t))) continue;

    // Prefer a device that costs us coverage on a skill this route does not use.
    const candidates = ownedDevices.filter(d =>
      partners.has(d.techniqueId) && partnerAllowed(skillId, d.techniqueId));
    if (candidates.length === 0) continue;
    const device = candidates.find(d => !routeSkills.has(getTechniqueSkill(d.techniqueId)))
      || candidates[0];

    const deviceSkill = getTechniqueSkill(device.techniqueId);
    if (char.equippedTechniqueGear[device.techniqueId]) continue; // already on

    for (const equipped of Object.keys(char.equippedTechniqueGear)) {
      if (getTechniqueSkill(equipped) === deviceSkill) {
        teamTechniques.delete(equipped);
        delete char.equippedTechniqueGear[equipped];
      }
    }
    char.equippedTechniqueGear[device.techniqueId] = device;
    teamTechniques.add(device.techniqueId);
    swaps++;
  }
  return swaps;
}
